/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import { useEffect, useMemo } from 'react';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useFilters } from '../contexts/FiltersContext';
import { getBrandHealthApiV3MetricsBrandsHealthPost, getBrandInsightsApiV3MetricsBrandsBrandIdInsightsPost, getDigitalBrandMetricsApiV3MetricsBrandsDigitalPost, HttpValidationError, TraditionalResponse } from '../config/api';
import { extractAxiosResponse } from '../config/axios.config';

export interface TraditionalMetrics {
  awareness: number;
  consideration: number;
  usage: number;
  recent_usage: number;
  primary_brand: number;
  trust: number;
  pride: number;
  innovation: number;
  value: number;
  quality: number;
  taste?: number;
  texture?: number;
  natural?: number;
  nps: number;
  loyalty: number;
  advocacy: number;
  satisfaction?: number;
  conversion_rate?: number;
  retention_rate?: number;
  total_responses: number;
}

export interface DigitalMetrics {
  sentiment_score: number;
  engagement_rate: number;
  share_of_voice: number;
  mentions_count: number;
  positive_mentions: number;
  negative_mentions: number;
  neutral_mentions: number;
  trending_topics: string[];
  influencer_reach: number;
  viral_coefficient: number;
}

export interface BrandMetrics {
  brand_id: string;
  brand_name: string;
  traditional: TraditionalMetrics;
  digital: DigitalMetrics;
  combined_score: number;
  category: string;
  last_updated: Date;
}

/**
 * Hook to fetch real brand metrics from the API
 * Separates Traditional (survey) and Digital (social media) metrics
 * In demo mode, uses monthly mock data based on the selected date
 */
export function useBrandMetrics() {
  const { selectedBrand, loading: filtersLoading, error: filterErrors, getFiltersByType } = useFilters()
  const { data: health, isPending: healthLoading, error: healthError, refetch } = useQuery({
    queryKey: ['v3', 'health-index', selectedBrand, getFiltersByType('traditional')],
    queryFn: () => extractAxiosResponse(getBrandHealthApiV3MetricsBrandsHealthPost({
      body: {
        brand_ids: [selectedBrand!],
        filters: getFiltersByType('traditional')
      },
    })).then(res => res && selectedBrand! in res? res[selectedBrand!]: null),
    enabled: !filtersLoading && !filterErrors && !!selectedBrand,
  })
  const { data: insights, isPending: insightsLoading, error: insightsError } = useQuery({
    queryKey: ['v3', 'insights', selectedBrand],
    queryFn: () => extractAxiosResponse(getBrandInsightsApiV3MetricsBrandsBrandIdInsightsPost({
      path: {brand_id: selectedBrand!}
    })),
    enabled: !filtersLoading && !filterErrors && !!selectedBrand,
  })
  // const {data: metrics, isPending: metricsLoading, error: metricsErrors, refetch} = useQuery({
  //   queryKey: ['v3', 'metrics', selectedBrand, getFiltersByType('traditional')],
  //   queryFn: () => extractAxiosResponse(getBrandMetricsApiV3MetricsBrandsTradPost({
  //     body: {
  //       brand_ids: [selectedBrand!],
  //       filters: getFiltersByType('traditional')
  //     },
  //   })).then(res => res && selectedBrand! in res? res[selectedBrand!]: null),
  //   enabled: !!selectedBrand,
  // })
  // const {data: digitalMetrics, isPending: digitalMetricsLoading, error: digitalMetricsErrors, refetch: digitalRefetch} = useQuery({
  //   queryKey: ['v3', 'digital-metrics', selectedBrand, getFiltersByType('digital')],
  //   queryFn: () => extractAxiosResponse(getDigitalBrandMetricsApiV3MetricsBrandsDigitalPost({
  //     body: {
  //       brand_ids: [selectedBrand!],
  //       filters: getFiltersByType('digital')
  //     },
  //   })).then(res => res && selectedBrand! in res? res[selectedBrand!]: null),
  //   enabled: !!selectedBrand,
  // })
  // const {data: demographicFilters} = useQuery({
  //   ...getFiltersApiV3MetricsFiltersGetOptions(),
  //   enabled: !isLoading && !meError,
  // })

  // const allBrands = useMemo(() => data?.current_org_brands || [], [data])
  const loading = useMemo(() => filtersLoading || healthLoading, [filtersLoading, healthLoading])
  const error = useMemo(() => filterErrors || healthError, [filterErrors, healthError])

  const compareBrands = async (brandIds: string[]) => {
    try {
      const promises = brandIds.map(id => axios.get(`/api/v2/metrics/brands/${id}`));
      const responses = await Promise.all(promises);
      return responses.map(r => r.data);
    } catch (err) {
      console.error('Failed to compare brands:', err);
      return [];
    }
  };

  return {
    health,
    loading,
    error,
    insights,
    insightsLoading,
    insightsError,
    getHealth: refetch,
    compareBrands
  };
}

export default useBrandMetrics;
